import { Copy } from "lucide-react";
import { toast } from "sonner";
import { StatusBanner } from "@/components/StatusBanner";
import PublicProfileView from "@/features/profiles/PublicProfileView";
import { ThemeControls } from "@/features/profiles/components/ThemeControls";
import { downloadVCard } from "@/features/profiles/vcard";

export function CanonicalUrl({ url, note = "Goes live after activation.", testPrefix = "profile" }) {
  const copy = () => navigator.clipboard?.writeText(url).then(() => toast.success("Link copied"), () => toast.error("Couldn't copy the link"));
  return (
    <div className="space-y-2">
      <p className="mw-eyebrow">Profile link</p>
      <div className="flex items-center gap-2">
        <p data-testid={`${testPrefix}-canonical-url`} className="min-w-0 flex-1 truncate rounded-md border border-border bg-muted px-3 py-3 font-mono text-xs">{url}</p>
        <button type="button" onClick={copy} data-testid={`${testPrefix}-copy-url-button`} aria-label="Copy link" className="mw-icon-btn border-border"><Copy className="h-4 w-4" /></button>
      </div>
      {note && <p data-testid={`${testPrefix}-activation-note`} className="text-xs text-muted-foreground">{note}</p>}
    </div>
  );
}

export function ProfilePhonePreview({ identity, theme, testId = "profile-preview" }) {
  return (
    <div data-testid={testId} className="mx-auto h-[760px] w-full max-w-[400px] overflow-y-auto rounded-[32px] border-[6px] border-foreground/90 bg-foreground/90 shadow-xl">
      <PublicProfileView identity={identity} theme={theme} onSaveContact={() => downloadVCard(identity)} />
    </div>
  );
}

export function ThemeLoadState({ t }) {
  if (t.load.status === "error") {
    return <div className="space-y-4 py-10"><StatusBanner>{t.load.error}</StatusBanner><button type="button" onClick={t.reload} data-testid="profile-retry-button" className="mw-btn mw-btn-ghost">Try again</button></div>;
  }
  return <div className="flex items-center gap-3 py-20"><span className="mw-spinner" /><span className="mw-eyebrow">Loading</span></div>;
}

export function ProfileThemeEditor({ t, footer, urlNote }) {
  return (
    <div data-testid="profile-theme-editor" className="grid grid-cols-1 gap-8 lg:grid-cols-[320px_1fr]">
      <div className="min-w-0 space-y-6">
        <section className="rounded-lg border border-border bg-card p-5 sm:p-6">
          <h2 className="mb-5 font-serif text-xl tracking-tight">Theme color</h2>
          <ThemeControls value={t.color} onChange={t.change} />
        </section>
        {t.identity.publicProfileUrl && (
          <section className="rounded-lg border border-border bg-card p-5 sm:p-6"><CanonicalUrl url={t.identity.publicProfileUrl} note={urlNote} /></section>
        )}
        {footer}
      </div>
      <div className="min-w-0 lg:sticky lg:top-8 lg:self-start">
        <p className="mw-eyebrow mb-4">Live preview</p>
        <ProfilePhonePreview identity={t.identity} theme={t.theme} />
      </div>
    </div>
  );
}
